/** AU-08 — hospital-cover premium reference data (PDF report, page 5).
 *
 * Backs the "Comparing hospital cover" page of the AU-08 PDF report (product
 * spec §5.3). The page lists, per Government cover tier, what a single-adult
 * hospital policy typically costs — a sourced, objective reference table. It is
 * information, not advice (validation plan §3): it never recommends a tier or an
 * insurer, never says "best for you", and never frames a saving. The user reads
 * the typical cost; nothing is steered.
 *
 * Architecture rule #1: this is reference data — a versioned, sourced, pure
 * lookup, never mutated after shipping. A new privatehealth.gov.au release gets
 * a NEW dataset file registered alongside the old one (same immutability rule as
 * the rulesets and the APRA benchmark). The medians are transcribed from a
 * generator run over the published dataset — see
 * `tools/refresh-au08-hospital-premiums.ts`.
 */

/**
 * The four Australian Government private-hospital cover tiers (Private Health
 * Insurance (Complying Product) Rules). Insurers also publish "+" variants
 * (Basic Plus, Bronze Plus, Silver Plus) that add clinical categories above the
 * tier minimum; we fold each "+" product into its base tier, since the report
 * compares at the four-tier level the Government defines. Gold has no "+" — it
 * must already cover every clinical category.
 */
export const HOSPITAL_TIERS = ["Basic", "Bronze", "Silver", "Gold"] as const;

export type HospitalTier = (typeof HOSPITAL_TIERS)[number];

/**
 * Premium statistics for one cover tier, computed over every OPEN (sellable)
 * single-adult hospital policy at that tier in the source release. Premiums are
 * the monthly premium at the full 100% rate (before any Australian Government
 * Rebate) — the `PremiumNoRebate` field privatehealth.gov.au publishes, so the
 * figure is comparable across people regardless of their rebate tier. All
 * figures are derived from the published per-policy premiums by pure arithmetic
 * (median / quartiles / counts); nothing is modelled.
 */
export interface HospitalTierPremium {
  /** Number of open single-adult policies at this tier in the source release. */
  policyCount: number;
  /** Number of distinct insurers offering an open single policy at this tier. */
  insurerCount: number;
  /** Median monthly premium (no rebate, 100%), AUD. */
  medianMonthlyPremium: number;
  /** 25th-percentile monthly premium (no rebate, 100%), AUD — the low end of
   * the typical range shown alongside the median. */
  p25MonthlyPremium: number;
  /** 75th-percentile monthly premium (no rebate, 100%), AUD — the high end. */
  p75MonthlyPremium: number;
}

/** A versioned hospital-premium dataset, keyed by the release it was built from. */
export interface Au08HospitalPremiumDataset {
  /** Stable id for the source release, e.g. "phis-2026-06". */
  id: string;
  /** Human label for the source release, e.g. "PrivateHealth.gov.au, 1 June
   * 2026 release". */
  period: string;
  /** Membership scale the premiums cover. Single only — the report shows a
   * single-adult reference cost (a couple/family figure would need its own
   * scale and is out of scope for the page). */
  scale: "single";
  /** What the premium figure represents, for the page caption. */
  premiumBasis: "monthly premium at the full 100% rate, before any rebate";
  /** Source-of-truth URLs the figures were derived from. */
  sources: Record<string, string>;
  /** Date the source was last verified, ISO. */
  sourcesVerifiedAt: string;
  /** Per-tier premium statistics. Every Government tier is present. */
  byTier: Readonly<Record<HospitalTier, HospitalTierPremium>>;
  /** Total open single-adult hospital policies across all tiers (a dataset
   * invariant — the per-tier `policyCount`s sum to this). */
  totalPolicies: number;
  /** Distinct insurers offering any open single hospital policy in the release. */
  totalInsurers: number;
}

/** Pure lookup: the premium statistics for one tier. */
export function tierPremium(
  dataset: Au08HospitalPremiumDataset,
  tier: HospitalTier,
): HospitalTierPremium {
  return dataset.byTier[tier];
}
